// src/App.js
import React, { useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, useLocation, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import RuralChoiceForm from './components/RuralChoiceForm';
import FirstChoiceForm from './components/FirstChoiceForm';
import SecondChoiceForm from './components/SecondChoiceForm';
import FirstResult from './components/FirstResult';
import SecondResult from './components/SecondResult';
import NewUserRegistration from './components/NewUserRegistration';
import ForgotPassword from './components/ForgotPassword';
import HospitalExchangeForm from './components/HospitalExchangeForm';
import FinalResult from './components/FinalResult';
import AdminDashboard from './components/AdminDashboard';
// import AdminLogin from './components/AdminLogin';
import ContactForm from './components/ContactForm';
import ApplicationStatus from './components/ApplicationStatus';
import ExchangeStatusPage from './components/ExchangeStatusPage';
import RuralExchangeStatusPage from './components/RuralExchangeStatusPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import RevisionHistory from './components/RevisionHistory';
import Disclaimer from './components/Disclaimer';
import StripePaymentInfo from './components/StripePaymentInfo';
import TermsOfService from './components/TermsOfService';
import Copyright from './components/Copyright';
import CommerceNotation from './components/CommerceNotation';
import StripePaymentPage from './components/StripePaymentPage';
import HospitalReportForm from './components/HospitalReportForm';
import HospitalReportListPage from './components/HospitalReportListPage';
import HospitalReportDetail from './components/HospitalReportDetail';
import PlacementStatus from './components/PlacementStatus';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import VotingResults from './components/VotingResults';
import RoundRobinRankings from './components/RoundRobinRankings';
import MyPage from './components/MyPage';
import VerifyEmail from './components/VerifyEmail';
import AllUserAssignments from './components/AllUserAssignments';
import FinalAssignments from './components/FinalAssignments';
import AdminCapacityManager from './components/AdminCapacityManager';
import AdminAnnouncementManager from './components/AdminAnnouncementManager';
import AdminFixedAssignmentManager from './components/AdminFixedAssignmentManager';
import RuralExchangePage from './components/RuralExchangePage';
import HelpPage from './components/HelpPage';
import AdminPaymentStatusPage from './components/AdminPaymentStatusPage';
import { SpeedInsights } from '@vercel/speed-insights/react';
import AuthCallback from './components/AuthCallback';

const ADSENSE_ALLOWED_PATH_PREFIXES = [
  '/dashboard',
  '/exchange-status',
  '/rural-exchange-status',
  '/first-result',
  '/second-result',
  '/final-result',
  '/final-assignments',
  '/placement-status',
  '/application-status',
  '/round-robin-rankings',
  '/voting-results',
  '/my-page',
  '/hospital-report-list',
  '/hospital-reports/',
];

// ★ Layout コンポーネントを作成してヘッダー・フッターの表示を制御
function AppLayout() {
  const location = useLocation();
  const gaMeasurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  const adsenseClientId = process.env.REACT_APP_ADSENSE_CLIENT_ID;
  // ヘッダー・フッターを非表示にするルートのリスト
  const hideHeaderRoutes = ['/login', '/register', '/forgot-password', '/reset-password', '/admin', '/verify-email', '/auth'];
  const hideFooterRoutes = ['/first-choice', '/rural-choice', '/exchange']; // 元のロジックを維持

  // location.pathname がこれらのパスで始まるかどうかで判断
  const shouldHideHeader = hideHeaderRoutes.some(path => location.pathname.startsWith(path));
  const shouldHideFooter = hideFooterRoutes.some(path => location.pathname.startsWith(path));
  const shouldEnableAdsense = location.pathname === '/' || ADSENSE_ALLOWED_PATH_PREFIXES.some(prefix =>
    location.pathname === prefix || location.pathname.startsWith(prefix)
  );

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      return;
    }
    if (!gaMeasurementId || typeof window === 'undefined' || typeof window.gtag !== 'function') {
      return;
    }
    const pagePath = `${location.pathname}${location.search}${location.hash}`;
    window.gtag('config', gaMeasurementId, {
      page_path: pagePath,
      page_title: document.title,
      page_location: window.location.href,
    });
  }, [gaMeasurementId, location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      return;
    }
    if (!adsenseClientId || !shouldEnableAdsense || typeof window === 'undefined') {
      return;
    }
    const scriptSrc = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adsenseClientId}`;
    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);
    if (!existingScript) {
      const script = document.createElement('script');
      script.async = true;
      script.src = scriptSrc;
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);
    }
  }, [adsenseClientId, shouldEnableAdsense, location.pathname]);

  return (
    <>
      {!shouldHideHeader && <Header />}
      {/* ★ 子ルートの内容がここにレンダリングされる */}
      <Outlet />
      {!shouldHideFooter && <Footer />}
    </>
  );
}

// ★ ルート定義を createBrowserRouter 用に構成
const router = createBrowserRouter([
  {
    // ★ AppLayout を共通のレイアウトとしてルート要素に設定
    element: <AppLayout />,
    // ★ ErrorBoundary など、必要に応じてエラーハンドリング要素を追加できます
    // errorElement: <ErrorBoundary />,
    children: [
      // ★ 各ルートをオブジェクト形式で定義
      { path: "/login", element: <Login /> },
      { path: "/verify-email", element: <VerifyEmail /> },
      { path: "/register", element: <NewUserRegistration /> },
      { path: "/forgot-password", element: <ForgotPassword /> },
      { path: "/auth/callback", element: <AuthCallback /> },
      { path: "/admin", element: <Navigate to="/admin-dashboard" replace /> },
      { path: "/dashboard", element: <PrivateRoute><Dashboard /></PrivateRoute> },
      { path: "/exchange-status", element: <PrivateRoute><ExchangeStatusPage /></PrivateRoute> },
      { path: "/rural-exchange-status", element: <PrivateRoute><RuralExchangeStatusPage /></PrivateRoute> },
      { path: "/rural-choice", element: <PrivateRoute><RuralChoiceForm /></PrivateRoute> },
      { path: "/first-choice", element: <PrivateRoute><FirstChoiceForm /></PrivateRoute> },
      { path: "/second-choice", element: <PrivateRoute><SecondChoiceForm /></PrivateRoute> },
      { path: "/first-result", element: <PrivateRoute><FirstResult /></PrivateRoute> },
      { path: "/second-result", element: <PrivateRoute><SecondResult /></PrivateRoute> },
      { path: "/exchange", element: <PrivateRoute><HospitalExchangeForm /></PrivateRoute> },
      { path: "/rural-exchange", element: <PrivateRoute><RuralExchangePage /></PrivateRoute> },
      { path: "/final-result", element: <PrivateRoute><FinalResult /></PrivateRoute> },
      { path: "/hospital-report", element: <PrivateRoute><HospitalReportForm /></PrivateRoute> },
      { path: "/hospital-report-list", element: <PrivateRoute><HospitalReportListPage /></PrivateRoute> },
      { path: "/hospital-reports/:report_id", element: <PrivateRoute><HospitalReportDetail /></PrivateRoute> },
      { path: "/placement-status", element: <PrivateRoute><PlacementStatus /></PrivateRoute> },
      { path: "/application-status", element: <PrivateRoute><ApplicationStatus /></PrivateRoute> },
      { path: "/admin-dashboard", element: <AdminRoute><AdminDashboard /></AdminRoute> },
      { path: "/admin/hospital-report", element: <AdminRoute><HospitalReportForm adminMode /></AdminRoute> },
      { path: "/admin/announcements", element: <AdminRoute><AdminAnnouncementManager /></AdminRoute> },
      { path: "/admin/capacity", element: <AdminRoute><AdminCapacityManager /></AdminRoute> },
      { path: "/all-user-assignments", element: <AdminRoute><AllUserAssignments /></AdminRoute> },
      { path: "/admin/assignments/fixed", element: <AdminRoute><AdminFixedAssignmentManager /></AdminRoute> },
      { path: "/admin/payment-status", element: <AdminRoute><AdminPaymentStatusPage /></AdminRoute> },
      { path: "/help", element: <HelpPage /> },
      { path: "/contact", element: <ContactForm /> },
      { path: "/round-robin-rankings", element: <PrivateRoute><RoundRobinRankings /></PrivateRoute> },
      { path: "/voting-results", element: <PrivateRoute><VotingResults /></PrivateRoute> },
      { path: "/my-page", element: <PrivateRoute><MyPage /></PrivateRoute> },
      { path: "/legal/privacy-policy", element: <PrivacyPolicy /> },
      { path: "/legal/revision-history", element: <RevisionHistory /> },
      { path: "/legal/disclaimer", element: <Disclaimer /> },
      { path: "/legal/stripe-payment-info", element: <StripePaymentInfo /> },
      { path: "/legal/terms-of-service", element: <TermsOfService /> },
      { path: "/legal/commerce-notation", element: <CommerceNotation /> },
      { path: "/legal/copyright", element: <Copyright /> },
      { path: "/final-assignments", element: <FinalAssignments isPublic={false} /> },
      { path: "/public-final-assignments", element: <FinalAssignments isPublic={true} /> },
      { path: "/payment", element: <StripePaymentPage /> },
      // { path: "/exam-taking", element: <ExamTakingPage /> },
      // { path: "/past-results", element: <PrivateRoute><PastResultsPage /></PrivateRoute> },
      // { path: "/results/:attemptId", element: <PrivateRoute><ResultDetailPage /></PrivateRoute> },
      { index: true, element: <PrivateRoute><Dashboard /></PrivateRoute> },
      { path: "*", element: <Navigate to="/dashboard" replace /> },
    ]
  }
], {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true,
  },
});

function App() {
  const isVercelAnalyticsEnabled =
    process.env.NODE_ENV === 'production' &&
    process.env.REACT_APP_DISABLE_VERCEL_ANALYTICS !== 'true';

  return (
    <AuthProvider>
      {/* ★ RouterProvider で router を提供 */}
      <RouterProvider router={router} future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }} />
      {isVercelAnalyticsEnabled && <SpeedInsights />}
    </AuthProvider>
  );
}

export default App;
